'use client'

import { yupResolver } from '@hookform/resolvers/yup'
import { useRouter } from 'next/navigation'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as y from 'yup'

import { Alert } from '@src/app/[country]/[lang]/checkout/modules/Alert'
import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { TextField } from '@src/components/TextField/TextField'
import { useSession } from '@src/hooks/useSession'
import { useLocale } from '@src/i18n/LocaleProvider'
import { ErrorCodes } from '@src/utils/constants.util'

const schema = y.object({
  email: y.string().required('email-required').email('email-invalid'),
  password: y.string().required('password-required'),
})

interface LoginModalFormProps {
  onClose: () => void
  isOpen: boolean
  redirectUrl?: string
}

export function LoginModalForm({ onClose, isOpen, redirectUrl }: LoginModalFormProps) {
  const { loginOAuth } = useSession()
  const { t } = useLocale('login-form')
  const router = useRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<ErrorCodes | null>(null)

  const form = useForm({
    defaultValues: { email: '', password: '' },
    resolver: yupResolver(schema),
  })

  // Close modal when pressing ESC
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose()
    }
    document.addEventListener('keydown', handleEsc)
    return () => document.removeEventListener('keydown', handleEsc)
  }, [isOpen, onClose])

  // Reset error and loading state when modal is closed
  useEffect(() => {
    if (isOpen) return
    setTimeout(() => {
      form.reset()
      setIsLoading(false)
      setError(null)
    }, 500)
  }, [isOpen, form])

  async function onSubmit() {
    try {
      setError(null)
      setIsLoading(true)
      const { email, password } = form.getValues()
      const { exchangeUrl } = await loginOAuth({ email, password, redirectUrl })
      router.push(exchangeUrl)
    } catch (error: any) {
      setIsLoading(false)
      setError(error?.code ?? ErrorCodes.AkamaiOAuthLogin)
    }
  }

  const errorMessage = useMemo(() => {
    if (!error) return null
    if (error === ErrorCodes.AkamaiOAuthInvalidCredentials) return t('invalid-credentials')
    else return t('unknown-error')
  }, [error, t])

  return (
    <div className="w-full">
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="mb-4 grid grid-flow-row gap-4">
          <TextField
            id="email"
            labelText={t('email')}
            type="text"
            disabled={isLoading}
            isInvalid={!!form.formState.errors.email?.message}
            captionText={t(form.formState.errors.email?.message as string)}
            {...form.register('email', { required: true })}
          />

          <TextField
            id="password"
            labelText={t('password')}
            type="password"
            disabled={isLoading}
            isInvalid={!!form.formState.errors.password?.message}
            captionText={t(form.formState.errors.password?.message as string)}
            {...form.register('password', { required: true })}
          />
        </div>

        {errorMessage && <Alert type="error" message={errorMessage} />}

        <Button
          type="submit"
          size="large"
          variant="primary"
          className="mb-4 mt-5 w-full"
          disabled={isLoading}
          isLoading={isLoading}
        >
          {t('cta')}
        </Button>
      </form>
      <div className="prose flex justify-center text-body-base">
        <LocalizedLink href="/forgot-password" data-testid="forgot-password">
          {t('forgot-password')}
        </LocalizedLink>
      </div>
    </div>
  )
}
